import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from 'axios';

import { API_KEY, URL, subscribeToConfigChanges } from './environment';
import storage from './storage';

// Recreate instance when config changes (similar to domains.ts pattern)

interface UserData {
  token?: string;
}

/** Default request deadline (ms) for the shared `axiosInstance` (strategist and other design calls inherit this). */
export const DEFAULT_AXIOS_REQUEST_TIMEOUT_MS = 1000 * 58;

interface AxiosInstanceConfig extends AxiosRequestConfig {
  headers: {
    'Content-Type': string;
    apikey: string;
    authorization?: string;
  };
}

/**
 * Create a reusable axios instance with proper authentication headers
 * This follows the same pattern as the Apollo client for consistency
 *
 * Features:
 * - Automatic authentication token injection
 * - Request/response interceptors for error handling
 * - Default request timeout ({@link DEFAULT_AXIOS_REQUEST_TIMEOUT_MS})
 * - Automatic recreation when environment config changes
 *
 * @returns {AxiosInstance} Configured axios instance with interceptors
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: URL,
    timeout: DEFAULT_AXIOS_REQUEST_TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
      apikey: API_KEY
    }
  } as AxiosInstanceConfig);

  // Add request interceptor to include authentication token
  instance.interceptors.request.use(
    (incoming: InternalAxiosRequestConfig) => {
      const user: UserData | null = storage.get('userData', true);

      if (!user?.token || !incoming.headers) {
        return incoming;
      }

      return {
        ...incoming,
        headers: {
          ...incoming.headers,
          authorization: user.token
        }
      } as unknown as InternalAxiosRequestConfig;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  // Add response interceptor for error handling
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        // Could trigger logout or token refresh here
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

let activeAxiosInstance: AxiosInstance = createAxiosInstance();

subscribeToConfigChanges(() => {
  activeAxiosInstance = createAxiosInstance();
});

/** Stable export: always forwards to the current environment-backed instance. */
const axiosInstance: AxiosInstance = new Proxy({} as AxiosInstance, {
  get(_target, prop) {
    const value = Reflect.get(
      activeAxiosInstance,
      prop,
      activeAxiosInstance
    ) as unknown;
    if (typeof value === 'function') {
      return (value as (...args: unknown[]) => unknown).bind(
        activeAxiosInstance
      );
    }
    return value;
  }
});

export default axiosInstance;
